import type {
  SummaryAggregationType,
  SummaryValueKind,
  TableSummaryState,
} from "./table.summary.types"

export const SUMMARY_STORAGE_PREFIX = "table-summary:"

export const DEFAULT_SUMMARY_STATE: TableSummaryState = {
  isVisible: false,
  aggregations: {},
}

/** Rendered when a calculation has nothing to work on (every value empty). */
export const SUMMARY_EMPTY_VALUE = "—"

/** Separates the entries of a plain list of values. */
export const SUMMARY_VALUES_SEPARATOR = ", "

/** Separates the "count× value" entries of a breakdown. */
export const SUMMARY_BREAKDOWN_SEPARATOR = " · "

/** The "no calculation" choice in a summary cell's menu. */
export const SUMMARY_NONE = "__none__"

/**
 * A summary describes the whole view, so while the row shows on a
 * server-paginated list, the table fetches the pages the operator hasn't
 * scrolled to — in pages this large, one after the other, since the list
 * API takes any page size and the cache appends pages in arrival order.
 */
export const SUMMARY_LOAD_ALL_PAGE_SIZE = 1000

/**
 * Above this many rows the table stops fetching the rest on its own: a
 * list that size (an event log) is too much to hold for a footer, so the
 * summary covers the loaded rows and says so.
 */
export const SUMMARY_LOAD_ALL_MAX_ROWS = 20_000

/**
 * Pages the table asks for without the list growing before it gives up,
 * so a failing request can't be retried forever.
 */
export const SUMMARY_LOAD_ALL_MAX_ATTEMPTS = 2

/**
 * How many breakdown entries get a colour of their own in the stacked bar;
 * the rest merge into one neutral "other" segment. Bound by the chart
 * palette's distinct hues — the neutral is the palette's last colour.
 */
export const SUMMARY_STACK_MAX_SEGMENTS = 8

/**
 * The calculations whose answer is a list rather than one figure. A figure
 * is set large, the way a totals line reads; a list keeps body size so more
 * of it fits.
 */
export const SUMMARY_LIST_AGGREGATIONS: SummaryAggregationType[] = [
  "values",
  "breakdown",
]

export const SUMMARY_AGGREGATION_LABELS: Record<
  SummaryAggregationType,
  string
> = {
  sum: "Sum",
  share: "Share",
  average: "Average",
  median: "Median",
  min: "Min",
  max: "Max",
  filled: "Filled",
  empty: "Empty",
  unique: "Unique",
  values: "Values",
  breakdown: "Breakdown",
}

/** Kind-specific overrides: on a date column min/max read as earliest/latest. */
export const SUMMARY_DATE_AGGREGATION_LABELS: Partial<
  Record<SummaryAggregationType, string>
> = {
  min: "Earliest",
  max: "Latest",
}

/**
 * The arithmetic a value kind supports, in menu order. A fraction column
 * runs every number calculation on both of its parts and adds the share the
 * summed parts make. Text has none: its cells only offer the counts and
 * lists below.
 */
export const SUMMARY_ARITHMETIC_AGGREGATIONS_BY_KIND: Record<
  SummaryValueKind,
  SummaryAggregationType[]
> = {
  number: ["sum", "average", "median", "min", "max"],
  fraction: ["sum", "share", "average", "median", "min", "max"],
  date: ["min", "max"],
  text: [],
}

/** The counts and lists every column supports, in menu order. */
export const SUMMARY_COUNT_AGGREGATIONS: SummaryAggregationType[] = [
  "filled",
  "empty",
  "unique",
  "values",
  "breakdown",
]
